/**
 * Real `getVaultSnapshot` — fetches the user's Vault rows from the
 * server's `GET /api/v1/vault` endpoint and maps them into the UI's
 * existing `VaultSnapshot` shape.
 *
 * ## Why this exists
 *
 * Until this milestone landed, the Vault page read its data
 * exclusively from `localStorage` via `mock.getVaultSnapshot`. That
 * meant clearing cache, switching browser, opening incognito, or
 * seeing a Railway redeploy land all wiped the user's "signed
 * entities" — the load-bearing user-facing surface had no
 * durability whatsoever.
 *
 * The server-side `vault_store` (SQLite, scoped by Supabase JWT
 * `sub`) is now the source of truth. This module is the client
 * adapter that lets the existing Vault page render unchanged from
 * server data.
 *
 * ## Field mapping
 *
 * The server's `VaultRow` is a subset of the UI's `GiSummary`:
 *
 *   - `root_id`, `version`, `name`, `description`, `author_name`
 *     project 1-for-1.
 *   - `domain` arrives as a Rust `Debug`-formatted string
 *     (`"Government"` / `"Custom(\"ai\")"`); we run it through the
 *     same `parseDomain` shape the marketplace uses.
 *   - `created_at` arrives as ISO 8601; we wrap it back into the
 *     `TrustedTimestamp` envelope with `source: 'Monotonic'` (the
 *     server doesn't yet propagate trusted-time provenance for the
 *     Vault list — that's a Phase-7+ refinement).
 *   - `status` defaults to `['signed']`. The Vault row records "I
 *     signed this," not the full lifecycle state machine; richer
 *     statuses come from the per-GI detail endpoint.
 *   - `stats` defaults to zero across the board until a server-side
 *     counters store lands. Today the Vault list is a record of
 *     ownership; the per-GI detail page is where per-entity counters
 *     surface.
 *
 * `activity` and the `stats` aggregate are derived locally from the
 * `gis` list so the UI's existing layout works without changes.
 */

import type {
  ActivityEvent,
  DomainType,
  GiStatus,
  GiSummary,
  TrustedTimestamp,
  VaultSnapshot,
  VaultStats,
} from '../types';
import { apiFetch } from '../http';

/** One row as the server returns it from `GET /api/v1/vault`. */
interface ServerVaultRow {
  root_id: string;
  version: string;
  name: string;
  description: string;
  /** Rust `Debug`-format of `DomainType`. See `parseDomain` below. */
  domain: string;
  author_name: string;
  /** ISO 8601 UTC string. */
  created_at: string;
  /** Engine storage `sequence` of the canonical entity entry. */
  sequence: number;
}

interface ServerVaultListResponse {
  gis: ServerVaultRow[];
}

/** Fetch the calling user's Vault snapshot. Requires a Supabase
 *  JWT in the `Authorization: Bearer …` header (apiFetch attaches
 *  it). Returns an empty snapshot — not an error — for a brand-new
 *  user with no rows yet. */
export async function getVaultSnapshot(): Promise<VaultSnapshot> {
  const resp = await apiFetch<ServerVaultListResponse>('/vault');
  const gis: GiSummary[] = (resp.gis ?? []).map(rowToSummary);
  const stats: VaultStats = {
    total_gis: gis.length,
    total_signatures: gis.length,
    total_verifications: 0,
    total_forks: 0,
  };
  // Activity feed is a Phase-7+ concern (server-side activity log).
  // Falling back to an empty list means the Vault page renders the
  // gis grid and an empty activity rail — the user's GIs are visible
  // immediately, which was the load-bearing fix.
  const activity: ActivityEvent[] = [];
  return { stats, gis, activity };
}

function rowToSummary(row: ServerVaultRow): GiSummary {
  return {
    root_id: row.root_id,
    version: row.version,
    name: row.name,
    description: row.description,
    domain: parseDomain(row.domain),
    author_name: row.author_name,
    created_at: parseCreatedAt(row.created_at),
    status: defaultStatus(),
    stats: {
      signatures: 1,
      verifications: 0,
      executions: 0,
      forks: 0,
    },
  };
}

/** Mirror of the marketplace's `parseDomain` — keep in sync. */
function parseDomain(raw: string): DomainType {
  switch (raw) {
    case 'Audio':
    case 'Process':
    case 'Medical':
    case 'Financial':
    case 'Defense':
    case 'Government':
    case 'Generic':
      return raw;
    default: {
      const match = /^Custom\("?([^")]+)"?\)$/.exec(raw);
      return { Custom: match?.[1] ?? raw };
    }
  }
}

function parseCreatedAt(iso: string): TrustedTimestamp {
  return {
    iso: iso || '1970-01-01T00:00:00.000Z',
    source: 'Monotonic',
  };
}

function defaultStatus(): GiStatus[] {
  return ['signed'];
}
